import { MaterialSymbol, type MaterialSymbolProps } from 'react-material-symbols'

/**
 * Single entry point for Material Design 3 (Material Symbols) icons.
 *
 * Wraps `react-material-symbols` so the rest of the app uses one consistent
 * default size / weight / color, and so swapping icon style (rounded →
 * outlined → sharp) later is a one-line change.
 *
 * Usage:
 *
 *   <Icon name="event_note" />                  // 20px, weight 400
 *   <Icon name="check" size={16} />             // smaller inline accent
 *   <Icon name="warning" color="var(--primary-fg)" fill />
 *
 * Names are typed against the full Material Symbols catalogue (~2.4k icons).
 * Browse https://fonts.google.com/icons?icon.set=Material+Symbols for picks.
 */

export type IconName = MaterialSymbolProps['icon']

export interface IconProps extends Omit<MaterialSymbolProps, 'icon'> {
  name: IconName
}

export function Icon({
  name,
  size = 20,
  weight = 400,
  grade = 0,
  ...rest
}: IconProps): JSX.Element {
  return (
    <MaterialSymbol
      icon={name}
      size={size}
      weight={weight}
      grade={grade}
      {...rest}
    />
  )
}
